import { List, Map, Set } from 'immutable';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import memoize from 'lodash/memoize';
import partition from 'lodash/partition';
import { createSelector } from 'reselect';

import * as StationConstants from './constants';
import * as D from './deps';

/**
 * @param  {object} state - the redux store state
 * @returns {string} - the stationId of user's current station selection
 */
export function getCurrentStationId(state) {
  // TODO this should be deprecated to use the more generic id getter
  return (
    (state.stations && state.stations.get(D.Constants.Station.StationConstants.CURRENT_STATION)) ||
    D.getInDomain('playback.utils.getCurrentPlaylist')(state)
  );
}

/**
 * @param  {object} state - the redux store state
 * @returns {List} - an immutable List of the current user's stationIds loaded into the app
 */
export function getMyStationIds(state) {
  return state.stations && state.stations.get(D.Constants.Station.StationConstants.STATIONS_KEY);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string | number} sourceId - check if this station is owned by the current user, consumer can pass either a stationId or pandoraId
 * @returns {boolean} - true if user owns this station, false if not
 */
export function isOwnStation(state, sourceId) {
  if (!sourceId) return false;
  // if pandoraId is passed, we convert it to a stationId
  const stationId = sourceId.toString().split(':').pop();
  return getMyStationIds(state).contains(stationId);
}

/**
 * @param  {object} state - the redux store state
 * @returns {List} - an immutable List of the current user's stationIds loaded into the app
 */
export function getAllTracksOnAllStationsForCurrentListener(state) {
  return state.stations.getIn([D.Constants.Station.StationConstants.STATION_HISTORY_KEY], List());
}

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an immutable List of the track tokens played on the currently selected station
 */
export const getStationHistory = createSelector(
  getCurrentStationId,
  getAllTracksOnAllStationsForCurrentListener,
  (stationId, stationHistory) => {
    return stationHistory
      .filter(h => h.get('stationId') === stationId)
      .map(h => h.get('trackToken'));
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an immutable List of the track tokens played on all stations in current session
 */
export const getAllStationHistories = createSelector(
  getAllTracksOnAllStationsForCurrentListener,
  stationHistory => {
    return stationHistory.map(h => h.get('trackToken'));
  },
);

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - station Id
 * @param  {Bool} onlyOwned - check only the current user's station entities
 * @returns {Immutable#Map} - an Immutable Map containing information about the station if found in the store, null otherwise
 */
export function getStationByStationId(state, stationId, onlyOwned) {
  if (onlyOwned && !getMyStationIds(state).includes(stationId)) {
    return null;
  }
  const stationEntities = D.Utils.Entity.getStationEntities(state);
  const station = stationEntities.get(stationId);
  return station ? station : null;
}

/**
 *
 * @param {object} stationEntities
 * @param {string} musicId
 * @returns {object | void}
 * @private
 */
export function _getStationByMusicId(stationEntities, musicId) {
  if (musicId) {
    // It is possible for a `musicId` and `initialSeed` to both be `undefined`
    return stationEntities.find(station => station.get('initialSeed') === musicId);
  }
}

/**
 *
 * @param {object} stationEntities
 * @returns {Function}
 * @private
 */
export function _memoizeGetStationByMusicId(stationEntities) {
  return memoize(musicId => _getStationByMusicId(stationEntities, musicId));
}

/**
 * Use case: you want to get a station by music id in an "efficient" way, most likely from within a component's `mapStateToProps` method.
 *
 * @param {object} state
 * @returns {Function} - A memoized function which takes a music id and returns a station
 */
export const createGetStationByMusicIdSelector = createSelector(
  D.Utils.Entity.getStationEntities,
  _memoizeGetStationByMusicId,
);

export function _getStationByPandoraId(stationEntities, pandoraId, idField = 'pandoraId') {
  return stationEntities.find(station => station.get(idField) === pandoraId);
}

export function _memoizeGetStationByPandoraId(stationEntities, idField) {
  return memoize(
    pandoraId => {
      return _getStationByPandoraId(stationEntities, pandoraId, idField);
    },
    pandoraId => `${pandoraId}+${idField}`,
  );
}

export const createGetStationByPandoraIdSelector = createSelector(
  D.Utils.Entity.getStationEntities,
  stations => {
    return _memoizeGetStationByPandoraId(stations, 'pandoraId');
  },
);

export const createGetStationByStationFactoryPandoraIdSelector = createSelector(
  D.Utils.Entity.getStationEntities,
  stations => {
    return _memoizeGetStationByPandoraId(stations, 'stationFactoryPandoraId');
  },
);

/**
 *
 * @param {object} state
 * @param {string} pandoraId
 * @returns {string} - the associated stationId
 */
export function getStationIdFromPandoraId(state, pandoraId) {
  const station = createGetStationByPandoraIdSelector(state)(pandoraId);
  return station && station.get('stationId');
}

export function getStationIdFromStationFactoryId(state, pandoraId) {
  const station = createGetStationByStationFactoryPandoraIdSelector(state)(pandoraId);
  return station && station.get('stationId');
}

export function getPandoraIdToMusicIdMap(state) {
  return state.entities
    .get('seeds')
    .map(seed => seed.get('pandoraId'))
    .flip();
}

export function _getStationByInitialSeedPandoraId(
  stationEntities,
  pandoraIdToMusicIdMap,
  pandoraId,
) {
  return _getStationByMusicId(stationEntities, pandoraIdToMusicIdMap.get(pandoraId));
}

export function _memoizeGetStationByInitialSeedPandoraId(stationEntities, pandoraIdToMusicIdMap) {
  return memoize(pandoraId =>
    _getStationByInitialSeedPandoraId(stationEntities, pandoraIdToMusicIdMap, pandoraId),
  );
}

export const createGetStationByInitialSeedPandoraIdSelector = createSelector(
  D.Utils.Entity.getStationEntities,
  getPandoraIdToMusicIdMap,
  _memoizeGetStationByInitialSeedPandoraId,
);

export function getStationIdFromSeedPandoraId(state, pandoraId) {
  const station = createGetStationByInitialSeedPandoraIdSelector(state)(pandoraId);
  return station && station.get('stationId');
}

/**
 *
 * @param {object} state
 * @param {string} pandoraId
 * @returns {boolean}
 */
export function isStationInitialSeedPlaying(state, pandoraId) {
  const entityDetails = D.Utils.Entity.getDetails(state, pandoraId);
  const station = getCurrentStation(state);
  if (!station) {
    return false;
  }

  const initialSeedMusicId = station.get('initialSeed');
  if (!initialSeedMusicId) {
    return false;
  }

  const currentlyPlayingEntity = D.Utils.Entity.getEntityById(state, initialSeedMusicId);
  if (!currentlyPlayingEntity) {
    return false;
  }

  return currentlyPlayingEntity.get('pandoraId') === entityDetails.get('pandoraId');
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - station Id
 * @returns {string} - The musicId of the initial seed of the
 station if found, otherwise null
 */
export function getInitialSeedByStationId(state, stationId) {
  const station = getStationByStationId(state, stationId);
  const initialSeed = station ? station.get('initialSeed') : null;
  return initialSeed ? initialSeed : null;
}

export function isGenreIdInMyStations(state, genreId) {
  return !!createGetStationByMusicIdSelector(state)(genreId);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} id - musicId
 * @returns {Map} - The seed associated with the musicId if found, otherwise null
 */
export function getSeedById(state, id) {
  const seed = state.entities.getIn(['seeds', id]);
  return seed ? seed : null;
}

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {boolean} - whether to show station recommendations or not
 */
export const showRecommendations = createSelector(getMyStationIds, stationIds => {
  return stationIds.size < D.Constants.Station.NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY;
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {object} params - params, the selector implicitly takes
 * params as its argument
 * @returns {string} - station name
 */
export const getStationName = createSelector(getStationByStationId, station => {
  return getName(station);
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {object} params - params, the selector implicitly takes
 * params as its argument
 * @returns {string} - station description
 */
export const getStationDescription = createSelector(getStationByStationId, station => {
  return station && station.get('description') ? station.get('description') : '';
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {object} params - params, the selector implicitly takes
 * params as its argument
 * @returns {string} - pandoraId
 */
export const getStationPandoraId = createSelector(getStationByStationId, station => {
  return station && station.get('pandoraId') ? station.get('pandoraId') : '';
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {object} params - params, the selector implicitly takes
 * params as its argument
 * @returns {bool} - station artist message setting
 */
export const getStationArtistMessagesSetting = createSelector(getStationByStationId, station => {
  return station && station.get('isArtistMessagesEnabled');
});

/**
 * @param  {object} station - A station
 * @returns {object} - dictionary containing like/dislike counts for a station
 */
export function getStationThumbsCount(station) {
  const thumbs = Map({
    thumbsUp: station.get('positiveFeedbackCount'),
    thumbsDown: station.get('negativeFeedbackCount'),
  });
  return thumbs;
}

/**
 * Is this station a shared station?
 *
 * @param {Immutable#Map} station - A station
 * @returns {bool} - Is it a shared station?
 */
export function getIsShared(station) {
  return station && station.get('isShared');
}

/**
 * Is this a hybrid station or brand station?
 *
 * @param {Immutable#Map} station - A station
 * @returns {boolean} - Is this a HybridStation?
 */
export function getIsAdvertiserStation(station) {
  return !!(station && station.get('isAdvertiserStation'));
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - station Id
 * @returns {boolean} - is the station a shared station
 */
export function getStationIsShared(state, stationId) {
  const station = getStationByStationId(state, stationId);

  return getIsShared(station);
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} stationId - station Id
 * @returns {boolean} - is the station an advertiser station
 */
export function getStationIsAdvertiser(state, stationId) {
  const station = getStationByStationId(state, stationId);
  return getIsAdvertiserStation(station);
}

/**
 * @param  {Immutable#Map} station - station
 * @param  {boolean} defaultDateCreated - if true and lastPlayed isn't available, value for
 *                                      dateCreated is returned
 * @returns {string} - the last time this station was played. Sometimes the server doesn't return
 *                  a lastPlayed time, for these stations we use the dateCreated field if
 *                  defaultDateCreated param is true or totalPlayTime is not 0.
 */
export function getStationLastPlayed(station, defaultDateCreated = true) {
  if (!station) {
    return null;
  }
  let lastPlayed = station.get('lastPlayed');
  const useDateCreated = defaultDateCreated || station.get('totalPlayTime') > 0;

  if (!lastPlayed && useDateCreated) {
    lastPlayed = station.get('dateCreated');
  }
  return lastPlayed;
}

/**
 * @param  {Immutable#Map} station - station
 * @returns {number} - the total play time of this station in seconds
 */
export function getStationPlayTime(station) {
  if (!station) {
    return null;
  }
  return parseInt(station.get('totalPlayTime'), 10) || 0;
}

/**
 * @param  {Immutable#Map} station - station
 * @returns {string} - the date the station was created
 */
export function getStationCreatedDate(station) {
  if (!station) {
    return null;
  }

  return station.get('dateCreated');
}

/**
 * @param  {object} state - the redux store state
 * @returns {boolean} - whether or not the user's stations have been loaded into the app
 */
export function listenerHasStations(state) {
  const myStations = getMyStationIds(state);
  return myStations.size !== 0;
}

/**
 * TODO Mystations page uses this. Needs to be refactored out
 *
 * @param  {object} state - the redux store state
 * @returns {List} - an Immutable List containing all the information about the user's stations.
 */
export function getMyStationList(state) {
  const stationIds = getMyStationIds(state);
  return stationIds.map(stationId => {
    return getStationByStationId(state, stationId);
  });
}

/**
 * @param  {object} state - the redux store state
 * @returns {Map} - an immutable Map representing the user's current station selection
 */
export function getCurrentStation(state) {
  const stationId = getCurrentStationId(state);
  return getStationByStationId(state, stationId);
}

/**
 * @param  {object} state - the redux store state
 * @returns {string} - the station id the user has elected to remove from their list
 */
export function getStationToBeRemoved(state) {
  const stationToBeRemoved = state.stations.getIn(
    [D.Constants.Station.StationConstants.STATION_TO_BE_REMOVED],
    List(),
  );

  return stationToBeRemoved ? stationToBeRemoved : null;
}

/**
 * @param {object} station - station to check if it's transformable
 * @returns {boolean} true if station can be transformed
 */
export function isStationTransformable(station) {
  return station && station.get('isTransformAllowed');
}

/**
 * When a createStation call updates the station data it will mark isNew=true if the station is new.
 *
 * @param {object} state - the redux store state
 * @param {string} stationId - station id
 * @returns {boolean} isNew - Is this station marked as new or is it simply un-deleted?
 */
export function getStationIsNew(state, stationId) {
  const station = getStationByStationId(state, stationId);
  return station && station.get('isNew');
}

/**
 * @param  {object} state - the redux store state
 * @param  {string} id - station Id
 * @returns {Map} - an Immutable Map containing information about the station if found in the store, null otherwise
 */
export function getStationByOriginalStationId(state, id) {
  const stationIds = getMyStationIds(state);
  const stationId = stationIds.find(s => {
    const stationEntity = getStationByStationId(state, s);
    return stationEntity ? stationEntity.get('originalStationId') === id : false;
  });

  return stationId ? getStationByStationId(state, stationId) : null;
}

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - stationId to look for the currently playing track, the selector implicitly takes
 * this as its argument
 * @returns {string} - currently playing trackToken
 */
export const getNowPlayingTrackToken = createSelector(getStationHistory, stationHistory => {
  return stationHistory.size ? stationHistory.get(stationHistory.size - 1) : null;
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable Map containing track info for the currently playing track.
 */
export function getNowPlayingTrack(state) {
  const trackToken = getNowPlayingTrackToken(state);
  return trackToken ? D.Utils.Entity.getTrackById(state, trackToken) : null;
}

export function isNowPlayingReplayedTrack(state) {
  const currentTrack = getNowPlayingTrack(state);
  return currentTrack && currentTrack.has(D.Constants.Playback.IS_REPLAYED_TRACK);
}

/**
 * @param  {string} musicId - musicId
 * @returns {boolean} - whether the musicId has a Station Key or not
 */
export function musicIdHasStationKey(musicId) {
  if (musicId == null) return false;
  if (musicId.length < 2) return false;
  const stationKey = musicId.charAt(0) + musicId.charAt(1);
  if (stationKey === D.Constants.Station.StationConstants.THUMBPRINT_RADIO) return true;
  const letterRegExp = /^[a-z]+$/;
  return letterRegExp.test(stationKey);
}

export function getStationCodeFromMusicId(musicId) {
  if (!musicId) {
    return null;
  } else if (musicId === D.Constants.Music.THUMBPRINT_RADIO_MUSIC_ID) {
    // The music id here is RR0
    return musicId;
  } else if (
    musicId.indexOf(D.Constants.Music.typeToMusicIdPrefix.advertiser) === 0 ||
    musicId.indexOf(D.Constants.Music.typeToMusicIdPrefix.artistStation) === 0
  ) {
    return D.Constants.Station.StationConstants.FRIENDS_STATION + musicId.substring(1);
  }

  return D.Constants.Station.StationConstants.MUSIC_ID_AC + musicId;
}

/**
 * @param  {string} musicId - musicId
 * @param {string} stationCodePrefix - if provided, used with musicId to generate
 * the station code (optional)
 * @returns {string} - station code
 */
export function generateStationCode(musicId, stationCodePrefix = null) {
  if (!musicId) {
    return null;
  } else if (isString(stationCodePrefix)) {
    return stationCodePrefix + musicId;
  }
  return getStationCodeFromMusicId(musicId);
}

/**
 * @param  {object} state - the redux store state
 * @returns {List} - recommended artists
 */
export function getArtistRecommendations(state) {
  return state.stations.getIn(
    [D.Constants.Station.StationConstants.RECOMMENDATION_RESULTS, 'artists'],
    List(),
  );
}

/**
 * @param  {object} state - the redux store state
 * @returns {List} - recommended genre stations
 */
export function getGenreStationRecommendations(state) {
  return state.stations.getIn(
    [D.Constants.Station.StationConstants.RECOMMENDATION_RESULTS, 'genreStations'],
    List(),
  );
}

export const getStationRecommendations = createSelector(
  getGenreStationRecommendations,
  getArtistRecommendations,
  (genres, artists) => {
    const recommendations = artists
      .concat(genres)
      .slice(0, StationConstants.MAX_RECOMMENDATIONS_TO_DISPLAY);
    return recommendations.map((entityId, position) => ({
      componentName: D.Constants.Station.StationConstants.STATIONS_YOU_MAY_LIKE,
      entityId,
      position,
      sourceType:
        position < artists.size
          ? D.Constants.Station.StationTypes.ARTIST_SUGGEST
          : D.Constants.Station.StationTypes.GENRE_SUGGEST,
      stationType:
        position < artists.size
          ? D.Constants.Station.StationConstants.ARTIST_SUGGEST
          : D.Constants.Station.StationConstants.GENRE_SUGGEST,
      totalStations: recommendations.size,
    }));
  },
);

export const areStationRecommendationsLoaded = createSelector(
  getGenreStationRecommendations,
  getArtistRecommendations,
  D.Utils.Entity.getGenreEntities,
  D.Utils.Entity.getArtistEntities,
  (genres, artists, genreEntities, artistEntities) =>
    genres.reduce(
      (isLoaded, genreId) => isLoaded && !!genreEntities.get(genreId, false),
      genres.size !== 0,
    ) &&
    artists.reduce(
      (isLoaded, artistId) => isLoaded && !!artistEntities.get(artistId, false),
      artists.size !== 0,
    ),
);

/**
 * To be used as a reducer function for summing listener counts across an many entities
 *
 * @param {number} total
 * @param {Immutable#Map} entity
 * @returns {number}
 * @private
 */
export function _totalListenerCountReducer(total, entity) {
  return (entity.get('listenerCount') || 0) + total;
}

/**
 *
 * @param {Immutable#Map} entities
 * @param {Immutable#List} filterIds
 * @returns {number}
 * @private
 */
export function _sumEntityListenerCounts(entities, filterIds) {
  const entitiesToSum = filterIds
    ? entities.filter((entity, token) => filterIds.includes(token))
    : entities;

  return entitiesToSum.reduce(_totalListenerCountReducer, 0);
}

/**
 *
 * @param {Immutable#Map} entities
 * @returns {Function} - A memoized function
 * @private
 */
export function _memoizeSumListenerCounts(entities) {
  return memoize(filterIds => _sumEntityListenerCounts(entities, filterIds));
}

/**
 * Use case: you want to sum the total number of listeners in a List of stations in an "efficient" way, most likely from within a component's `mapStateToProps` method.
 *
 * @param {object} state
 * @returns {Function} - A memoized function which takes a List of entity ids and sums all their "listener counts".
 */
export const createSumGenresListenerCountSelector = createSelector(
  D.Utils.Entity.getGenreEntities,
  _memoizeSumListenerCounts,
);

/**
 * @todo - eventually these getters should go into some kind of general API entity abstraction layer
 * @param {Immutable#Map} station
 * @returns {string}
 */
export function getName(station) {
  return station ? station.get('name', '') : '';
}

// Gets the shuffle station for the current listener
export function getShuffleStationId(state) {
  return state.stations.getIn([D.Constants.Station.StationConstants.SHUFFLE_STATION], '');
}

// Gets the station entity of the current listener's shuffle station
export function getShuffleStationEntity(state) {
  const id = getShuffleStationId(state);
  const entities = D.Utils.Entity.getStationEntities(state);
  return entities && entities.get(id, null);
}

export const getPreviousShuffleSet = createSelector(getShuffleStationEntity, stationEntity => {
  if (stationEntity) {
    return new Set(stationEntity.get('previousShuffleStationIds'));
  }
  return new Set();
});

// Gets the Set of all stations in a user's station list that can be shuffled
export const getShuffleableStationIdsSet = createSelector(
  getMyStationIds,
  D.Utils.Entity.getStationEntities,
  (listenerStationIds, entities) => {
    let entity;
    const canShuffleIds = listenerStationIds.filter(id => {
      entity = entities.get(id);
      return entity ? !!entity.get('canShuffleStation') : false;
    });
    return new Set(canShuffleIds);
  },
);

// Gets the List of shuffleStationIds from the listener's shuffle station
// removes any stations that were deleted by filtering on the available stations
// and converts to Set (and memoizes result) for easy membership checks

export const getShuffleStationIdSet = createSelector(
  getShuffleStationEntity,
  getShuffleableStationIdsSet,
  (stationEntity, available) => {
    const active = stationEntity ? stationEntity.get('shuffleStationIds') : [];
    return new Set(active.filter(stationId => available.has(stationId)));
  },
);

export const shouldShowShuffle = createSelector(getShuffleableStationIdsSet, shuffleSet => {
  return shuffleSet.size > 1;
});

/**
 * Use case: You need to compare the number of actively shuffling stations against the number available stations
 * Only used as a reference to update IS_IN_SHUFFLE_ALL_MODE in state
 * If you need to check if the UI is in shuffle all mode use isInShuffleAllMode instead
 *
 * @param {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {boolean} - Returns true if the user is in shuffle all mode and false if the user is in custom mode. This is reflected in the UI.
 */
export const areAllStationsShuffled = createSelector(
  getShuffleableStationIdsSet,
  getShuffleStationIdSet,
  (available, active) => {
    return available.size === active.size;
  },
);

export const canPlayPreviousShuffle = createSelector(
  areAllStationsShuffled,
  getPreviousShuffleSet,
  (allNowShuffled, previousShuffle) => {
    return !!(allNowShuffled && previousShuffle && previousShuffle.size);
  },
);

export function isArraySizeOfShuffleableSet(state, arr = []) {
  return getShuffleableStationIdsSet(state).size === arr.length;
}

// Returns whether shuffle is on in the UI
export function isInShuffleMode(state) {
  return state.stations.getIn([D.Constants.Station.StationConstants.IS_IN_SHUFFLE_MODE], null);
}

/**
 * Use case: You want to check if the user is in shuffle all mode or in custom mode.
 * Tells you whether custom or shuffle all is reflected in the UI
 * Unless you need to directly compare the number of available stations to the number of actively shuffling stations,
 * use this instead of areAllStationsShuffled
 *
 * @param {object} state - the redux store state
 * @returns {boolean} - Returns true if the user is in shuffle all mode and false if the user is in custom mode. This is reflected in the UI.
 */
export function isInShuffleAllMode(state) {
  return state.stations.getIn([D.Constants.Station.StationConstants.IS_IN_SHUFFLE_ALL_MODE], null);
}

export function isStationShuffling(state, id) {
  const track = getNowPlayingTrack(state);
  if (!track || !isInShuffleMode(state)) return null;
  return track.get('stationId') === id;
}

export function isShuffleStation(state, id) {
  const station = getStationByStationId(state, id);
  return station ? !!station.get('isShuffle') : false;
}

// Checks whether a station Id is in the shuffle set. If it is, that means
// we are shuffling it.
export function isStationIdInShuffle(state, stationId) {
  const shuffleSet = getShuffleStationIdSet(state);
  return shuffleSet && shuffleSet.has(stationId);
}

// Checks whether a station Id is in the 'available to shuffle' set. If it is, that means
// we can shuffle it (but aren't necessarily doing so already).
export function isAvailableToShuffle(state, stationId) {
  const shuffleSet = getShuffleableStationIdsSet(state);
  return shuffleSet && shuffleSet.has(stationId);
}

/*
 * Two letter prefixes in stationIds drive station creation
 * behavior on the backend.
 */
export function getStationIdPrefix(stationId) {
  if (!isString(stationId) || stationId.length < 2) {
    return null;
  }

  const prefix = stationId.substr(0, 2);
  if (isNaN(parseInt(prefix, 10))) {
    return prefix;
  }
  return null;
}

export function isStationCurrent(state, stationId) {
  return stationId === getCurrentStationId(state);
}

/*
 * Returns station Id for current station details page.
 */
export function getStationId(state) {
  return state.stationDetails.get(D.Constants.StationDetails.keys.STATION_ID);
}

export function getCurrentListenerStationsThatHaveReceivedFragments(state) {
  return state.stations.get('stationsThatHaveReceivedFragments');
}

// SORTING
/*
 Returns a pruned list of station ids that includes all of the standard stations
 a user has. This will not include thumbprint or shuffle stations. The results
 of this function are used as inputs to the different sorting functions that
 populate the MyStations page
 */
export const getSortableStationIds = createSelector(
  getMyStationIds,
  getShuffleStationId,
  (stationIds, shuffleStationId) => {
    return stationIds.filter(val => {
      return val !== shuffleStationId;
    });
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable List of the current user's stations sorted by:
 * recency (most recent -> least recent)
 */
export const getRecencySortedStationIds = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (myStationIds, stationEntities) => {
    return myStationIds.sort((a, b) => {
      const aDate = getStationLastPlayed(stationEntities.get(a));
      const bDate = getStationLastPlayed(stationEntities.get(b));
      return new Date(bDate) - new Date(aDate);
    });
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable List of the current user's stations sorted by:
 * playtime (lowest playtime -> highest playtime)
 */
export const getPlaytimeSortedStationIds = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (myStationIds, stationEntities) => {
    return myStationIds.sort((a, b) => {
      return (
        getStationPlayTime(stationEntities.get(a)) - getStationPlayTime(stationEntities.get(b))
      );
    });
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable List of the current user's stations sorted by:
 * alphebetic order of station names in the current locale (for USA: # -> a -> z)
 */
export const getAlphabeticSortedStationIds = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (myStationIds, stationEntities) => {
    return myStationIds.sortBy(
      id => stationEntities.getIn([id, 'name'], ''),
      D.Utils.StringUtils.alphabetizeComparator,
    );
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable List of the current user's stations sorted by:
 * date collected (most recent -> least recent)
 */
export const getRecentlyCollectedSortedStationIds = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (myStationIds, stationEntities) => {
    return myStationIds.sort((a, b) => {
      const aDate = getStationCreatedDate(stationEntities.get(a));
      const bDate = getStationCreatedDate(stationEntities.get(b));

      return new Date(bDate) - new Date(aDate);
    });
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {Map} - a map of sorting types to immutable lists of sorted station ids
 */
export const getUpdatedStationIdSorts = createSelector(
  getRecentlyCollectedSortedStationIds,
  getRecencySortedStationIds,
  getPlaytimeSortedStationIds,
  getAlphabeticSortedStationIds,
  (createdTime, recency, playTime, alphabetic) => {
    return Map({
      alphabetic,
      createdTime,
      recency,
      playTime,
    });
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {orderedMap} - an alphabetically sorted map the user's station names to those station's ids
 */
export const getStationNameToIdMap = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (myStationIds, stationEntities) => {
    let unsortedNameToIdMap = Map(); // map of `station name` -> `station ID`
    for (const stationId of myStationIds) {
      unsortedNameToIdMap = unsortedNameToIdMap.set(
        stationEntities.getIn([stationId, 'name']),
        stationId,
      );
    }

    // sort into alphabetical orderedMap by key (station name)
    return unsortedNameToIdMap.sortBy((val, key) => key, D.Utils.StringUtils.alphabetizeComparator);
  },
);

export const sortStationsByDate = (ids, { entities, dateProperty }) => {
  return ids.sort((a, b) => {
    const bDate = new Date(entities.getIn([b, dateProperty], 0));
    const aDate = new Date(entities.getIn([a, dateProperty], 0));
    return bDate - aDate;
  });
};

export const getRecentlyModifiedStationsByDate = (ids, { entities, dateProperty }) => {
  const sortedStations = sortStationsByDate(ids, { entities, dateProperty });
  return sortedStations
    .slice(0, D.Constants.Station.NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY)
    .map(id => {
      return {
        id,
        pandoraType: D.Constants.SourceTypes.PandoraTypes.STATION,
        lastPlayed: entities.getIn([id, 'lastPlayed'], null),
        dateCreated: entities.getIn([id, 'dateCreated'], null),
      };
    });
};

export const getRecentStationsForNowPlaying = createSelector(
  getSortableStationIds,
  D.Utils.Entity.getStationEntities,
  (ids, entities) => {
    const stationIds = ids && ids.size ? ids.toJS() : [];
    const [lastPlayed, dateCreated] = partition(stationIds, id => {
      return entities.getIn([id, 'lastPlayed'], null);
    });
    const lastPlayedSorted = getRecentlyModifiedStationsByDate(lastPlayed, {
      entities,
      dateProperty: 'lastPlayed',
    });
    const dateCreatedSorted = getRecentlyModifiedStationsByDate(dateCreated, {
      entities,
      dateProperty: 'dateCreated',
    });
    return [lastPlayedSorted, dateCreatedSorted];
  },
);

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @returns {List} - an Immutable List containing the five most recent stations
 * sorted on the client by recency
 */
export const getRecentlyPlayedForNowPlaying = createSelector(
  getRecentStationsForNowPlaying,
  getRecentPodcasts,
  (stations, podcasts) => {
    const [lastPlayedSorted, dateCreatedSorted] = stations;
    const recentItems = [...lastPlayedSorted, ...podcasts].filter(item => item);
    const sortedRecentItems = recentItems.sort((a, b) => {
      return new Date(b.lastPlayed) - new Date(a.lastPlayed);
    });

    if (sortedRecentItems.length >= D.Constants.Station.NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY) {
      return List(
        sortedRecentItems.slice(0, D.Constants.Station.NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY),
      );
    }

    const mixedRecentItems = [...sortedRecentItems, ...dateCreatedSorted];
    return List(mixedRecentItems.slice(0, D.Constants.Station.NOW_PLAYING_RECENT_ITEMS_TO_DISPLAY));
  },
);

export function getRecentPodcasts(state) {
  return D.getInDomain('collection.utils.getRecentlyPlayedPodcasts', List())(state);
}

export function findStationEntityByMusicId(state, musicId) {
  const stations = D.Utils.Entity.getStationEntities(state);
  const stationIds = getMyStationIds(state);
  const userStations = stationIds.map(stationId => {
    return stations.get(stationId, Map());
  });
  return userStations.find(station => {
    if (musicId === D.Constants.Music.THUMBPRINT_RADIO_MUSIC_ID) {
      return station.get('isThumbprint');
    }
    return station.get('initialSeed') === musicId;
  });
}

/**
 * Returns the lastPlayed station based on what's stored in redux store,
 *
 * @param  {object} state - the redux store state
 * @returns {string} - the last played stationId, if any, null otherwise
 */
export function getLastPlayedStationId(state) {
  const stationIds = getRecencySortedStationIds(state);

  if (stationIds.size) {
    return stationIds.get(0);
  }

  return null;
}

/**
 * Returns a list of feedbackIds of station
 *
 * @param  {object} state - the redux store state
 * @param  {string} stationId - the station we need feedback from
 * @returns {List} - list of feedbackIds
 */
export function getStationFeedbackIds(state, stationId) {
  return state.feedback.get(D.Constants.Feedback.Keys.FEEDBACK_IDS).get(stationId);
}

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - station to check if TPR
 * @returns {Bool} - returns true if station is Thumbprint Radio
 */
export const isThumbprintStationId = createSelector(getStationByStationId, station => {
  return station && station.get('isThumbprint');
});

/**
 * Returns station id of TPR
 *
 * @param  {object} state - the redux store state
 * @returns {string} - returns stationId if station is Thumbprint Radio
 */
export function getThumbprintStationId(state) {
  const stationId = getMyStationIds(state).find(entry => {
    return isThumbprintStationId(state, entry);
  });
  return stationId;
}

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - station to check if thumbprint count
 * @returns {number} - returns thumbprintThumbCount of user
 */
export const getThumbprintthumbCount = createSelector(getStationByStationId, station => {
  return station && station.get('thumbprintThumbCount');
});

/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - station to check if adding seed is allowed
 * @returns {boolean} - returns true if user allowed to add/edit seeds
 */
export const allowAddSeed = createSelector(getStationByStationId, station => {
  return station && station.get('allowAddSeed');
});
/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - station to check if adding seed is allowed
 * @returns {boolean} - returns true if user allowed to edit station details
 */
export const isEditable = createSelector(getStationByStationId, station => {
  return station && (station.get('allowRename') || station.get('allowEditDescription'));
});

export function shouldLaunchAddVariety(state) {
  return (
    state.stationDetails &&
    state.stationDetails.get(D.Constants.StationDetails.keys.LAUNCH_ADD_VARIETY)
  );
}

// Gets the most recently played station that is not a shuffle station
// currently used to start playing a station after shuffle ends
export const mostRecentNonShuffleStation = createSelector(
  getRecencySortedStationIds,
  recencySortedIds => {
    return recencySortedIds.get(0, null);
  },
);

export const createGetMyStationsByFactoryPandoraIdSelector = createSelector(
  state => getMyStationIds(state),
  state => D.Utils.Entity.getStationEntities(state),
  (stationIds, stations) => {
    return Map(
      stations
        .valueSeq()
        .filter(station => {
          return stationIds.includes(station.get('stationId'));
        })
        .map(station => {
          return [station.get('stationFactoryPandoraId'), station];
        }),
    );
  },
);

export function isPandoraIdInMyStations(state, pandoraId) {
  return createGetMyStationsByFactoryPandoraIdSelector(state).has(pandoraId);
}

export function getMyStationByFactoryPandoraId(state, pandoraId) {
  return createGetMyStationsByFactoryPandoraIdSelector(state).get(pandoraId);
}

/*
  voice tracks are a special case since some of their information is in the
  track, and some is in the catalog
*/
export function isHostedStation(state, stationId) {
  if (isStationPandoraId(stationId)) {
    return D.getInDomain('catalog.utils.isCatalogHostedStation', false)(state, stationId);
  }
  const station = getStationByStationId(state, stationId, false);
  return !!station && station.getIn(['hybridStation', 'isHosted'], false);
}

export function getVoiceTrackAttributes(state, track) {
  if (D.Utils.VoiceTrack.isStationVoiceTrack(track)) {
    const annotation = D.getInDomain('catalog.utils.getAnnotations')(state, track.get('pandoraId'));
    if (annotation) {
      const authorId = annotation.get('authorId');
      const authorAnnotation = D.getInDomain('catalog.utils.getAnnotations')(state, authorId);
      const title = annotation.get('name');
      const buttonUrlWeb = annotation.get('buttonUrlWeb');
      const buttonText = annotation.get('buttonText');
      if (authorAnnotation) {
        const artistName = authorAnnotation.get('name');
        return {
          title,
          artistName,
          buttonUrlWeb,
          buttonText,
        };
      }
      return {
        title,
        buttonUrlWeb,
        buttonText,
      };
    }
  }
  return {};
}

export function getTrackNarrativeText(track) {
  const narrativeData = track.get('narrative');
  let narrative;

  if (narrativeData) {
    if (narrativeData.get('paragraph')) {
      narrative = narrativeData.get('paragraph');
    } else {
      narrative = `${narrativeData.get('intro')} ${narrativeData.get('focusTraits').join(', ')}.`;
    }
  }
  return narrative;
}

export function getIndexOfFeedback(state, id, stationId) {
  const tracks = getStationFeedbackIds(state, stationId);
  if (tracks) {
    return tracks.toJS().findIndex(track => track && track === id);
  }
}

export function getFeaturedContentType(state, artist, lyrics) {
  const hasArtistEvents =
    artist &&
    !D.getInDomain('catalog.utils.getArtistEventsFromState', List())(
      state,
      artist.get('pandoraId'),
    ).isEmpty();

  if (hasArtistEvents) {
    return StationConstants.FilletConstants.EVENTS;
  }
  if (lyrics) {
    return StationConstants.FilletConstants.LYRICS;
  }
  if (artist && artist.get('bio')) {
    return StationConstants.FilletConstants.BIO;
  }
  if (artist && artist.get('similarArtists')) {
    return StationConstants.FilletConstants.SIMILAR_ARTISTS;
  }
  return null;
}

export function findTrackTokensByMusicIdAndStation(state, musicId, stationId) {
  const playlistTracks = D.Utils.Entity.getTrackEntitiesFromState(state);
  return playlistTracks.reduce((results, track) => {
    if (track.get('musicId') === musicId && track.get('stationId') === stationId) {
      results.push(track.get('trackToken'));
    }
    return results;
  }, []);
}

export function allowFeedbackForTrack(state, track) {
  if (!track || D.Utils.Playlist.isArtistMessage(track)) {
    return false;
  }
  if (track.get('allowFeedback')) {
    return true;
  }
  // track gets feedback if it's a shared station but not shared thumbprint
  const station = getStationByStationId(state, track.get('stationId'));
  // TODO: remove the isThumbprint check after RADIO-20419 is resolved (shared TPR should be non-transformable)
  return isStationTransformable(station) && !station.get('isThumbprint');
}

export function isStationNarrativeDisplayed(state) {
  return state.stationNarrative.get(D.Constants.Narratives.STATION_NARRATIVE_IS_SHOWN);
}

export function isStationNarrativeShowing(state, stationId) {
  // check if the particular station has narrative displayed
  // that isn't hidden by user choice; otherwise just return if
  // it should be showing
  const hasNarrative = isStationNarrativeDisplayed(state);
  let isHidden = false;
  if (stationId) {
    isHidden = state.stationNarrative
      .get(D.Constants.Narratives.STATION_NARRATIVES_HIDDEN)
      .contains(stationId);
  }
  return hasNarrative && !isHidden;
}

export function getSeedByPandoraId(state, pandoraId) {
  return state.entities.get('seeds').find(seed => seed.get('pandoraId') === pandoraId);
}

export const getVideoAdsDisabledForCurrentStation = createSelector(
  state => state.stationDetails,
  stationDetails => stationDetails.get(D.Constants.StationDetails.keys.VIDEO_ADS_DISABLED),
);

/*
 * Returns true if stationId is a pandoraId, otherwise returns false
 */
export function isStationPandoraId(stationId) {
  if (isString(stationId)) {
    return stationId.startsWith(D.Constants.SourceTypes.PandoraTypes.STATION);
  }
  return false;
}

export function isGenrePandoraId(stationId) {
  return isPandoraIdOfSourceType(D.Constants.SourceTypes.PandoraTypes.GENRE, stationId);
}

export function isGenreSeedId(seedId) {
  return isPandoraIdOfSourceType(D.Constants.SourceTypes.GenreSeedId, seedId);
}

function isPandoraIdOfSourceType(sourceType, id) {
  if (isString(id)) {
    return id.startsWith(sourceType);
  }
  return false;
}
/**
 * @param  {object} state - the redux store state, the selector implicitly takes
 * state as its argument
 * @param  {string} stationId - station to check if thumbprint count
 * @returns {Bool} - returns isThumbprintProcessSkips flag from api response
 */
export const isThumbprintProcessSkips = createSelector(getStationByStationId, station => {
  return station && station.get('thumbprintProcessSkips');
});

export function getTotalStationCount(state) {
  return state.stations.get(D.Constants.Station.StationConstants.TOTAL_STATION_COUNT);
}

function getNumLoadedStations(state) {
  const stationIds = getMyStationIds(state);
  return stationIds ? stationIds.size : 0;
}

export function areAllStationsLoaded(state) {
  // < just to be safe, but total station count should only ever be equal or greater
  // than number of loaded stations
  return getTotalStationCount(state) <= getNumLoadedStations(state);
}

export function hasTakeoverModes(station) {
  if (!station) {
    return null;
  }
  return station.get('hasTakeoverModes');
}

export function getInteractiveRadioModes(state) {
  return state.stations.get(StationConstants.keys.INTERACTIVE_RADIO_AVAILABLE_MODES, List());
}

export function hasArtistTakeoverModes(state) {
  return state.stations.get(StationConstants.keys.HAS_TAKEOVER_MODES, false);
}

export function getArtistTakeoverModes(state, stationId) {
  const station = getStationByStationId(state, stationId);

  const modes = station && station.get('modes');
  return modes ? modes.get('takeoverModes') : List();
}

export function getArtistTakeoverModesHeader(state, stationId) {
  const station = getStationByStationId(state, stationId);

  const modes = station && station.get('modes');
  return modes && modes.get('takeoverModesHeader');
}

export function getInteractiveRadioCurrentModeId(state) {
  return state.stations.get(StationConstants.keys.INTERACTIVE_RADIO_MODE, '');
}

export function getInteractiveRadioCurrentModeName(state) {
  const currentModeId = getInteractiveRadioCurrentModeId(state);
  const currentMode = getInteractiveRadioModes(state).find(
    mode => mode.get('modeId') === currentModeId,
  );
  return currentMode && currentMode.get('modeName', '');
}

export function getInteractiveRadioAuthorPandoraId(state) {
  const megastarMode = getInteractiveRadioModes(state).find(mode => mode.get('authorPandoraId'));
  return showArtistTakeoverModes(state) && megastarMode.get('authorPandoraId')
    ? megastarMode.get('authorPandoraId')
    : null;
}

export function getInteractiveRadioModeById(state, modeId) {
  if (!isNumber(modeId)) return Map();
  return getInteractiveRadioModes(state).find(mode => mode.get('modeId') === modeId) || Map();
}

export function isInteractiveRadioAvailable(state) {
  return !getInteractiveRadioModes(state).isEmpty() && getInteractiveRadioModes(state).size > 1;
}

export function getShouldShortCircuitAfterGetFragment(state) {
  return state.stations.get(
    D.Constants.Station.StationConstants.SHOULD_SHORT_CIRCUIT_AFTER_GET_FRAGMENT,
  );
}

function getIsRightRailActive(state) {
  return D.getInDomain('ads.utils.getIsRightRailActive', false)(state);
}

export const getIsStationListCollapsible = createSelector(
  getIsRightRailActive,
  D.Utils.UIState.getIsTrackTemplateRendered,
  (isRightRailActive, isTrackTemplateRendered) => {
    return isRightRailActive || isTrackTemplateRendered;
  },
);

export function getNextStationId(state) {
  return state.stations.get(D.Constants.Station.StationConstants.NEXT_STATION);
}

export function isInEditMode(state) {
  return state.stations.get(D.Constants.Station.StationConstants.IS_IN_EDIT_MODE);
}

export function showArtistTakeoverModes(state) {
  return hasArtistTakeoverModes(state);
}

export function shouldSetInteractiveRadioStartupModeId(state) {
  const currentPathname = D.Utils.Route.getCurrentPathnameFromState(state);
  const isPathInNowPlayingPath = D.Utils.Route.isPathInNowPlayingPath(currentPathname);
  const modeId = D.Utils.QueryParams.getInteractiveRadioStartupModeId(state);
  const currentModeId = getInteractiveRadioCurrentModeId(state);
  const isDifferentModeId = currentModeId !== modeId;

  return isPathInNowPlayingPath && isDifferentModeId && modeId >= 0;
}

export function getSourceIdFromStationId(stationId) {
  if (!stationId) {
    return '';
  }

  return `${D.Constants.SourceTypes.PandoraTypes.USER_STATION}:0:${stationId}`;
}
